/**
 * L'octave à laquelle une portée s'écrit : le 8va et le 8vb de la frise.
 *
 * Pur : ni React, ni DOM, ni transport. La gravure du crochet est dans
 * `score-layout.ts` ; ce qui est ici est la décision, où elle se teste.
 *
 * ## Une commodité de gravure, décidée ici et non lue dans la source
 *
 * Le 8va n'est pas une hauteur. C'est une façon d'écrire des notes qui
 * demanderaient trop de lignes supplémentaires, et la théorie du projet le dit
 * dans ces termes : une information de mise en page, que l'analyse peut ignorer
 * et qu'on ne garde que si l'on régénère une gravure. Voir
 * `docs/theorie-musicale/12-notation-gravure-musicxml.md`, § 1.3.
 *
 * La frise régénère une gravure, mais elle ne peut pas recopier celle de la
 * source. L'analyseur ne garde pas le 8va, puisque le MusicXML porte déjà la
 * hauteur jouée et que c'est elle qui compte pour tout le reste ; un MIDI n'en
 * a jamais ; et aux niveaux fabriqués, les notes ne sont pas celles de la
 * partition. Elle décide donc elle-même, sur les notes qu'elle grave, comme un
 * graveur le ferait.
 *
 * *The Sound of Silence* a montré pourquoi il le faut : sa gauche est écrite à
 * l'octave des mesures 2 à 18, elle sonne entre ré4 et ré5, et la frise la
 * dessinait avec jusqu'à cinq lignes au-dessus de la portée de fa, là où la
 * partition d'origine l'écrit sur la portée.
 *
 * ## La règle
 *
 * Un **passage** est une suite de frappes qui ne revient jamais sur la portée :
 * toutes ses notes sont au-dessus de la ligne du haut (pour un 8va) ou sous la
 * ligne du bas (pour un 8vb). Un passage qui demande plus de trois lignes
 * supplémentaires sur deux frappes au moins s'écrit à l'octave, de sa première
 * frappe à sa dernière.
 *
 * - Une note isolée garde ses lignes : un crochet pour une seule note coûte
 *   plus à lire que les lignes qu'il épargne.
 * - Le passage s'arrête dès qu'une note revient sur la portée, et non au
 *   premier retour sous le seuil : un crochet qui s'ouvrirait et se fermerait à
 *   chaque note d'un arpège serait illisible.
 * - Décalé d'une octave, un passage retombe sur la portée ou juste au-dessus :
 *   ses notes sont toutes hors de la portée, donc sept degrés plus bas elles
 *   sont encore au-dessus de sa ligne du bas. L'écriture à l'octave ne crée
 *   jamais de lignes de l'autre côté.
 * - Un silence d'une mesure ou plus coupe le passage : un crochet tendu
 *   au-dessus de mesures vides ne dit plus rien.
 *
 * Trois lignes est une **convention** : aucun chapitre de la théorie du projet
 * ne fixe de seuil, trois est l'usage des graveurs, et c'est un paramètre.
 */

import { topStep, type Staff } from "@/lib/harmony/staff";

/** Une octave, en degrés de la portée. */
export const OCTAVE_STEPS = 7;

/**
 * À partir de combien de lignes supplémentaires on écrit à l'octave.
 *
 * Plus de trois, donc quatre : c'est la convention de ce module, voir l'en-tête.
 */
export const OTTAVA_LEDGERS = 4;

/** Combien de frappes du passage doivent demander autant de lignes. */
const OTTAVA_MIN_STRIKES = 2;

/** Un passage écrit à l'octave. */
export type Ottava = {
  readonly fromTick: number;
  /** La fin de la dernière frappe du passage. */
  readonly toTick: number;
  /**
   * `1` pour un 8va : les notes s'écrivent une octave **sous** ce qu'elles
   * sonnent. `-1` pour un 8vb, l'inverse.
   */
  readonly shift: 1 | -1;
  /**
   * Le degré **écrit** de la note la plus éloignée de la portée, une fois le
   * passage décalé : la plus haute sous un 8va, la plus basse sous un 8vb.
   *
   * C'est au-delà d'elle que le crochet se pose, et la gravure en a besoin
   * avant de savoir combien de place donner à la portée.
   */
  readonly edge: number;
};

/** Ce que la règle lit d'une frappe : quand, et à quelle hauteur. */
type Strike = {
  readonly fromTick: number;
  readonly toTick: number;
  readonly keys: readonly { readonly step: number }[];
};

/**
 * Les passages d'une portée à écrire à l'octave, dans l'ordre du temps.
 *
 * Les frappes sont celles d'une seule main, dans l'ordre, et à leur hauteur
 * **jouée** : c'est justement ce que ce module décide de ne pas écrire tel quel.
 * `barTicks` coupe un passage sur un silence d'une mesure ; zéro, on ne coupe
 * pas.
 */
export function ottavasOf(strikes: readonly Strike[], staff: Staff, barTicks = 0): Ottava[] {
  const out: Ottava[] = [];

  for (const shift of [1, -1] as const) {
    let run: Strike[] = [];

    for (const strike of strikes) {
      if (strike.keys.length === 0) {
        continue;
      }

      const outside = beyond(strike, staff, shift);
      const last = run[run.length - 1];
      const silent = last !== undefined && barTicks > 0 && strike.fromTick - last.toTick >= barTicks;

      if (!outside || silent) {
        push(out, run, staff, shift);
        run = [];
      }

      if (outside) {
        run.push(strike);
      }
    }

    push(out, run, staff, shift);
  }

  return out.sort((a, b) => a.fromTick - b.fromTick);
}

/**
 * Le décalage en vigueur à cet instant : `1` sous un 8va, `-1` sous un 8vb,
 * `0` ailleurs.
 *
 * Étendue fermée à gauche et ouverte à droite, comme les noms de la frise.
 */
export function shiftAt(ottavas: readonly Ottava[], tick: number): 0 | 1 | -1 {
  for (const ottava of ottavas) {
    if (ottava.fromTick <= tick && tick < ottava.toTick) {
      return ottava.shift;
    }
  }

  return 0;
}

/** Le degré où une note s'écrit sous ce décalage. */
export function writtenStep(step: number, shift: 0 | 1 | -1): number {
  return step - OCTAVE_STEPS * shift;
}

/** Le passage, s'il mérite d'être écrit à l'octave. */
function push(out: Ottava[], run: readonly Strike[], staff: Staff, shift: 1 | -1): void {
  const first = run[0];
  const last = run[run.length - 1];

  if (first === undefined || last === undefined) {
    return;
  }

  if (run.filter((strike) => far(strike, staff, shift)).length < OTTAVA_MIN_STRIKES) {
    return;
  }

  const steps = run.flatMap((strike) => strike.keys.map((key) => key.step));
  const edge = shift === 1 ? Math.max(...steps) : Math.min(...steps);

  out.push({ fromTick: first.fromTick, toTick: last.toTick, shift, edge: writtenStep(edge, shift) });
}

/** Toute la frappe est hors de la portée, du côté du décalage. */
function beyond(strike: Strike, staff: Staff, shift: 1 | -1): boolean {
  const steps = strike.keys.map((key) => key.step);

  return shift === 1 ? Math.min(...steps) > topStep(staff) : Math.max(...steps) < staff.bottomStep;
}

/** Une note de la frappe demande plus de trois lignes supplémentaires. */
function far(strike: Strike, staff: Staff, shift: 1 | -1): boolean {
  const steps = strike.keys.map((key) => key.step);
  const reach = 2 * OTTAVA_LEDGERS;

  return shift === 1 ? Math.max(...steps) >= topStep(staff) + reach : Math.min(...steps) <= staff.bottomStep - reach;
}
